import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '/v2/api';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request interceptor: inject JWT ──────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('tokkam_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Response interceptor: handle 401 / device conflict ──────────────────────
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const msg = err.response?.data?.message || '';
    if (err.response?.status === 401) {
      if (msg.includes('deja utilise')) {
        window.dispatchEvent(new CustomEvent('tokkam:device-conflict'));
      } else {
        window.dispatchEvent(new CustomEvent('tokkam:unauthorized'));
      }
    }
    return Promise.reject(err);
  }
);

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (phoneNumber, password) =>
    api.post('/auth/login', { phoneNumber, password }),

  logout: () => api.post('/auth/logout'),

  refreshToken: () => api.post('/auth/refresh'),

  // Admin-specific login (role: admin/supervisor)
  adminLogin: (phoneNumber, password, adminKey) =>
    api.post('/auth/admin/login', { phoneNumber, password, adminKey }),
};

// ─── INFRASTRUCTURE / METRICS ─────────────────────────────────────────────────
export const infraAPI = {
  // Server health checks (ping each server)
  getServerHealth: () => api.get('/admin/infra/health'),

  // System metrics for a specific server
  getServerMetrics: (serverId) =>
    api.get(`/admin/infra/metrics/${serverId}`),

  // Historical metrics (last N hours)
  getMetricsHistory: (serverId, hours = 24) =>
    api.get(`/admin/infra/metrics/${serverId}/history`, { params: { hours } }),

  // Active sessions count
  getActiveSessions: () => api.get('/admin/infra/sessions/active'),

  // Redis stats
  getRedisStats: () => api.get('/admin/infra/redis/stats'),

  // Database stats
  getDbStats: () => api.get('/admin/infra/db/stats'),
};

// ─── USERS / SUBSCRIBERS ──────────────────────────────────────────────────────
export const usersAPI = {
  // All parents/subscribers
  getParents: (page = 1, limit = 50, search = '') =>
    api.get('/admin/users/parents', { params: { page, limit, search } }),

  // Single parent detail
  getParent: (userId) => api.get(`/admin/users/parents/${userId}`),

  // Suspend / reactivate
  updateStatus: (userId, status) =>
    api.patch(`/admin/users/${userId}/status`, { status }),

  // Delete account
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),

  // User's active sessions
  getUserSessions: (userId) =>
    api.get(`/admin/users/${userId}/sessions`),

  // Force logout all devices
  forceLogout: (userId) =>
    api.post(`/admin/users/${userId}/sessions/invalidate`),

  // Support N2: trigger mobile app password reset OTP
  resetMobilePassword: (phoneNumber) =>
    api.post('/support/password-reset-otp', null, { params: { phone: phoneNumber } }),
};

// ─── CHILDREN ─────────────────────────────────────────────────────────────────
export const childrenAPI = {
  // All children
  getChildren: (page = 1, limit = 50, search = '') =>
    api.get('/admin/children', { params: { page, limit, search } }),

  // Children by parent
  getChildrenByParent: (parentId) =>
    api.get(`/admin/children/by-parent/${parentId}`),

  // Single child with last position
  getChild: (childId) => api.get(`/admin/children/${childId}`),

  // Child location history
  getLocationHistory: (childId, limit = 20) =>
    api.get(`/admin/children/${childId}/locations`, { params: { limit } }),

  // Last known position
  getLastPosition: (childId) =>
    api.get(`/admin/children/${childId}/position/last`),

  // Admin only: unlink a parent or co-parent from a child
  unlinkParent: (childId, parentId, reason = '') =>
    api.post(`/admin/children/${childId}/unlink-parent`, { parentId, reason }),

  // Admin only: remove a child from active monitoring
  deleteChild: (childId) =>
    api.delete(`/admin/children/${childId}`),
};

// ─── TRACKERS (READ-ONLY SUPPORT) ────────────────────────────────────────────
export const trackersAPI = {
  getTrackers: (params = {}) => api.get('/admin/trackers', { params }),
  getGatewayProtocols: () => api.get('/admin/trackers/gateway-protocols'),
  unlinkChild: (trackerId, reason = '') => api.post(`/admin/trackers/${trackerId}/unlink-child`, { reason }),
  unlinkOwner: (trackerId, reason = '') => api.post(`/admin/trackers/${trackerId}/unlink-owner`, { reason }),
};

// ─── LOCATION SEARCH ──────────────────────────────────────────────────────────
export const locationAPI = {
  // Search by parent phone number
  searchByParentPhone: (phoneNumber) =>
    api.get('/admin/location/search', { params: { phoneNumber } }),

  // Get all children positions for a parent
  getParentChildrenPositions: (parentId) =>
    api.get(`/admin/location/parent/${parentId}/children`),

  // Live position feed (SSE endpoint URL)
  getLiveFeedUrl: (childId) =>
    `${BASE_URL}/admin/location/live/${childId}?token=${localStorage.getItem('tokkam_admin_token')}`,
};

// ─── ALARMS ───────────────────────────────────────────────────────────────────
export const alarmsAPI = {
  // Active alarms
  getActive: () => api.get('/admin/alarms/active'),

  // Alarm history
  getHistory: (limit = 50) =>
    api.get('/admin/alarms/history', { params: { limit } }),

  // SOS alerts
  getSOS: () => api.get('/admin/alarms/sos'),

  // Acknowledge alarm
  acknowledge: (alarmId) =>
    api.post(`/admin/alarms/${alarmId}/ack`),

  // Acknowledge all
  acknowledgeAll: () => api.post('/admin/alarms/ack-all'),

  // Resolve SOS
  resolveSOS: (sosId, note) =>
    api.post(`/admin/alarms/sos/${sosId}/resolve`, { note }),
};

// ─── PAYMENTS ─────────────────────────────────────────────────────────────────────────
export const paymentsAPI = {
  list: (params = {}) => api.get('/admin/payments', { params }),
};


// ─── COMMERCIAL TERRAIN ───────────────────────────────────────────────────────
export const commercialAPI = {
  dashboard: () => api.get('/admin/commercial/dashboard'),
  referralEvents: ({ limit = 10, search = '', page = 1 } = {}) =>
    api.get('/admin/commercial/referral-events', { params: { limit, search, page } }),
  saveAgent: (payload) => api.post('/admin/commercial/agents', payload),
  saveLead: (payload) => api.post('/admin/commercial/leads', payload),
  updateLeadStatus: (payload) => api.post('/admin/commercial/leads/status', payload),
};

// ─── OPERATORS / SUPPORT ACCOUNTS ─────────────────────────────────────────────
export const operatorsAPI = {
  list: () => api.get('/admin/operators'),
  save: (payload) => api.post('/admin/operators', payload),
};

// ─── STATS / DASHBOARD ────────────────────────────────────────────────────────
export const statsAPI = {
  // Global dashboard stats
  getDashboard: () => api.get('/admin/stats/dashboard'),

  // Growth chart data
  getGrowthData: (period = '30d') =>
    api.get('/admin/stats/growth', { params: { period } }),

  // Revenue data
  getRevenue: (period = '30d') =>
    api.get('/admin/stats/revenue', { params: { period } }),
};

export default api;
